import type { SmartViewArtifactVersion } from "../../../../../api/types/smartViewArtifactsTypes";
import type { ArtifactScreen } from "../canvas/ArtifactFrame";
import { findSourceTitle, findViewTitle } from "../canvas/checkResult";
import type { SourceNote } from "../canvas/sourceRequestsState";
import type { PickedPart } from "./pickedParts";

const MAX_BUILDER_VIEW_LENGTH = 8_000;
const MAX_SOURCE_MESSAGE_LENGTH = 300;
const MAX_LISTED_SOURCES = 20;
const PICKS_HEADING = "Parts the user pointed at, in the order picked:";

const describeValues = (values: string[], labels: Record<string, string> | undefined): string =>
  values.length === 0
    ? "all values"
    : values.map((value) => (labels?.[value] ? `${value} (${labels[value]})` : value)).join(", ");

const describeOpenView = (version: SmartViewArtifactVersion, { view, params }: ArtifactScreen): string => {
  if (view === "") {
    return "Open view: the main view.";
  }
  const title = findViewTitle(version, view) || view;
  const clicked = Object.entries(params).map(([key, values]) => `${key} = ${values.join(", ")}`);
  return `Open view: "${title}" (${view})${clicked.length > 0 ? `, opened for ${clicked.join("; ")}` : ""}.`;
};

const describeSource = (version: SmartViewArtifactVersion, { sourceKey, message }: SourceNote): string => {
  const title = findSourceTitle(version, sourceKey) || sourceKey;
  const named = `"${title}" (${sourceKey})`;
  return message === undefined ? named : `${named}: ${message.slice(0, MAX_SOURCE_MESSAGE_LENGTH)}`;
};

const describeSourceList = (
  version: SmartViewArtifactVersion,
  heading: string,
  sources: SourceNote[]
): string | undefined =>
  sources.length === 0
    ? undefined
    : [heading, ...sources.slice(0, MAX_LISTED_SOURCES).map((source) => `- ${describeSource(version, source)}`)].join(
        "\n"
      );

export const describeBuilderView = (
  version: SmartViewArtifactVersion,
  screen: ArtifactScreen,
  filterValues: Record<string, string[]>,
  filterLabels: Record<string, Record<string, string>>
): string => {
  const filters = version.filters.map(
    ({ key, label }) => `${label} (${key}) = ${describeValues(filterValues[key] ?? [], filterLabels[key])}`
  );
  const failed = screen.sourceNotes.filter(({ kind }) => kind === "failed");
  const empty = screen.sourceNotes.filter(({ kind }) => kind === "empty");
  return [
    describeOpenView(version, screen),
    filters.length > 0 ? `Filter values: ${filters.join("; ")}.` : undefined,
    describeSourceList(version, "Sources that failed here:", failed),
    describeSourceList(version, "Sources that returned no rows here:", empty),
  ]
    .filter((line) => line !== undefined)
    .join("\n")
    .slice(0, MAX_BUILDER_VIEW_LENGTH);
};

export const joinBuilderView = (parts: PickedPart[], screen: string | undefined): string | undefined => {
  if (parts.length === 0) {
    return screen || undefined;
  }
  const picks = [PICKS_HEADING, ...parts.map(({ line }, index) => `${index + 1}. ${line}`)].join("\n");
  return screen ? `${picks}\n${screen.slice(0, MAX_BUILDER_VIEW_LENGTH - picks.length - 1)}` : picks;
};
